import { useCallback, useMemo, useRef } from 'react';
import useFetchCollection, { type IFetchCollectionPayload } from '@/creditflowresearch/infrastructure/state/useFetchCollection';
import { getContainer } from '@framework/di/getContainer';
import type {
  ICorporateBondScreenerTrancheResponse,
  ICorporateBondScreenerTrancheCollectionResponse,
} from '@/creditflowresearch/domain/vo/Bondscreener/ICorporateBondScreenerTranche';
import type { TFilterPayload } from '@/creditflowresearch/domain/vo/Bondscreener';
import BondScreenersRepository from '@/creditflowresearch/infrastructure/repository/BondScreenersRepository';
import { withRowType } from '@/creditflowresearch/application/BondScreener/withRowType';
import { ROW_TYPE_TRANCHE } from '@/creditflowresearch/application/BondScreener/rowType';
import { buildCorporateBondScreenerRequest } from './buildCorporateBondScreenerRequest';
import useFilterQuerySync from './useFilterQuerySync';

export type ICorporateBondScreenerTrancheRow =
  ICorporateBondScreenerTrancheResponse & { rowType: typeof ROW_TYPE_TRANCHE };

export const tagTranche = withRowType(ROW_TYPE_TRANCHE);

const useCorporateBondScreener = (
  initialFilters: TFilterPayload,
  initialTranches?: ICorporateBondScreenerTrancheCollectionResponse,
  columns?: readonly string[],
) => {
  const container = getContainer();
  const repository = container.get<BondScreenersRepository>(BondScreenersRepository);

  const totalCount = initialTranches?.totalCount ?? 0;
  const collection = useMemo(
    () => (initialTranches?.collection ?? []).map(tagTranche),
    [initialTranches],
  );
  const skipNextFetch = useRef(collection.length > 0);

  const { filterPayload, applyFilters } = useFilterQuerySync(initialFilters);

  const fetchTranches = useCallback(async (payload: IFetchCollectionPayload) => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return { collection, totalCount };
    }

    const response = await repository.getTranches(buildCorporateBondScreenerRequest({
      sortBy: payload.sortBy as string,
      sortOrder: payload.sortOrder as string,
      page: payload.page as number,
      perPage: payload.perPage as number,
      search: payload.search as string,
      filters: payload.filters as TFilterPayload,
      columns: payload.columns as string[] | undefined,
    }));

    return { collection: response.collection.map(tagTranche), totalCount: response.totalCount };
  }, []);

  const screenerCollection = useFetchCollection<ICorporateBondScreenerTrancheRow>(
    fetchTranches,
    {
      payload: { filters: filterPayload, columns: columns ? [...columns] : undefined },
      initialValues: { collection, totalCount, sortBy: 'date', sortOrder: 'desc' as const },
    },
  );

  return {
    ...screenerCollection,
    filterPayload,
    setFilterPayload: applyFilters,
  };
};

export default useCorporateBondScreener;
